import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useT } from '../lib/i18n';
import { Network as NetIcon } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, PillToggle, PageHeader, QueryState, CALUM } from '../components/charts';
import DateRangeControl from '../components/DateRangeControl';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { formatNumber } from '../lib/format';
import { todayISO } from '../lib/day';

// category → colour
const CAT_COLORS = {
  Drinks: CALUM.gold, Food: CALUM.green, Glaces: CALUM.clay, Extras: CALUM.mint,
  Producten: CALUM.slate, 'Bouillons & Autre': '#a78bfa', Onbekend: '#cbd5e1',
};
const catColor = (c) => CAT_COLORS[c] || '#94a3b8';
// raw backend categories → plain Dutch (translated via t())
const CAT_LABEL = { Drinks: 'Dranken', Food: 'Eten', Glaces: 'IJs', Extras: 'Diversen', Onbekend: 'Onbekend' };

const W = 900, H = 560;
// phones have no hover: selection happens by tapping (hover stays on desktop)
const CAN_HOVER = typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover)').matches;

// tiny deterministic force-directed layout (no dependency)
function layout(nodes, edges) {
  const N = nodes.length;
  if (!N) return {};
  const pos = {};
  nodes.forEach((n, i) => {
    const a = (2 * Math.PI * i) / N;
    pos[n.id] = { x: W / 2 + Math.cos(a) * 220, y: H / 2 + Math.sin(a) * 200, vx: 0, vy: 0 };
  });
  const maxC = Math.max(...edges.map((e) => e.count), 1);
  const E = edges.map((e) => ({ s: e.source, t: e.target, w: e.count / maxC }));
  const REP = 26000, GRAV = 0.011, DAMP = 0.82, DT = 0.9;
  for (let it = 0; it < 600; it++) {
    // repulsion (all pairs)
    for (let i = 0; i < N; i++) {
      const a = pos[nodes[i].id];
      for (let j = i + 1; j < N; j++) {
        const b = pos[nodes[j].id];
        let dx = a.x - b.x, dy = a.y - b.y;
        let d2 = dx * dx + dy * dy || 0.01;
        const f = REP / d2;
        const d = Math.sqrt(d2);
        const fx = (dx / d) * f, fy = (dy / d) * f;
        a.vx += fx; a.vy += fy; b.vx -= fx; b.vy -= fy;
      }
    }
    // springs (edges) — stronger pairs pull closer
    E.forEach((e) => {
      const a = pos[e.s], b = pos[e.t];
      let dx = b.x - a.x, dy = b.y - a.y;
      const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
      const L = 95;
      const f = (d - L) * (0.01 + 0.045 * e.w);
      const fx = (dx / d) * f, fy = (dy / d) * f;
      a.vx += fx; a.vy += fy; b.vx -= fx; b.vy -= fy;
    });
    // gravity to centre + integrate
    nodes.forEach((n) => {
      const p = pos[n.id];
      p.vx += (W / 2 - p.x) * GRAV; p.vy += (H / 2 - p.y) * GRAV;
      p.vx *= DAMP; p.vy *= DAMP;
      p.x += p.vx * DT; p.y += p.vy * DT;
      p.x = Math.max(30, Math.min(W - 30, p.x));
      p.y = Math.max(30, Math.min(H - 30, p.y));
    });
  }
  return pos;
}

export default function Relations() {
  const t = useT();
  const [range, setRange] = useState({ preset: 'today', start_date: todayISO(), end_date: todayISO() });   // open on the current day
  const p = { start_date: range.start_date, end_date: range.end_date };
  const [metric, setMetric] = useState('count');   // edge thickness: count | lift
  const [minCount, setMinCount] = useState(30);
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);  // tapped/clicked node (sticky)
  const active = selected || hover;

  const nw = useApi(() => api.productNetwork(p), [range.start_date, range.end_date]);
  const nodes = useMemo(() => nw.data?.nodes || [], [nw.data]);
  const edges = useMemo(() => nw.data?.edges || [], [nw.data]);

  // The SVG scales with its container (viewBox 900×560). On a phone that shrinks
  // labels below legibility, so measure the width and scale text + tap targets
  // in viewBox units (k = viewBox px per screen px, 1 on desktop, ~2.5 on phones).
  const wrapRef = useRef(null);
  const [k, setK] = useState(1);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const upd = () => setK(Math.min(2.6, Math.max(1, W / (el.clientWidth || W))));
    upd();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, [nw.data]);

  const pos = useMemo(() => layout(nodes, edges), [nodes, edges]);
  const shown = useMemo(() => edges.filter((e) => e.count >= minCount), [edges, minCount]);

  // scales
  const maxNode = Math.max(...nodes.map((n) => n.count), 1);
  const r = (c) => 7 + 20 * Math.sqrt(c / maxNode);
  const maxEdge = Math.max(...shown.map((e) => e[metric]), 1);
  const ew = (e) => 1 + 6 * (e[metric] / maxEdge);

  const neighbors = useMemo(() => {
    if (!active) return null;
    const set = new Set([active]);
    shown.forEach((e) => { if (e.source === active) set.add(e.target); if (e.target === active) set.add(e.source); });
    return set;
  }, [active, shown]);

  const nameOf = (id) => nodes.find((n) => n.id === id)?.name || id;
  const strongest = [...edges].sort((a, b) => b.count - a.count).slice(0, 10);
  const cats = [...new Set(nodes.map((n) => n.category))];

  return (
    <PageShell page="Productrelaties" accent="gold">
      <PageHeader title={t("Productrelaties")} subtitle={t("Welke producten worden samen gekocht — netwerk van combinaties.")}>
        <DateRangeControl value={range} onChange={setRange} dataRange={nw.data?.window && { start: nw.data.window.start, end: nw.data.window.end }} />
      </PageHeader>

      <QueryState q={nw} isEmpty={(d) => !d.nodes?.length}>
        {() => (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <KpiTile label={t("Bonnen geanalyseerd")} value={formatNumber(nw.data.baskets)} sub={t("met ≥1 product")} accent="gold" icon={NetIcon} />
            <KpiTile label={t("Producten (knopen)")} value={formatNumber(nodes.length)} sub={t("meest verkocht")} accent="green" />
            <KpiTile label={t("Combinaties (lijnen)")} value={formatNumber(shown.length)} sub={`≥ ${minCount}× ${t("samen")}`} accent="mint" />
            <KpiTile label={t("Sterkste duo")} value={strongest[0] ? `${strongest[0].count}×` : '—'}
              sub={strongest[0] ? `${nameOf(strongest[0].source)} + ${nameOf(strongest[0].target)}` : ''} accent="gold" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SectionCard className="lg:col-span-2" title={t("Netwerk — samen gekocht")}
              subtitle={`${t("Bol = product (grootte = hoe vaak verkocht) · lijn = samen gekocht (dikte = sterkte).")} ${t("Tik op een bol om de relaties te zien.")}`}
              right={<PillToggle value={metric} onChange={setMetric} options={[{ value: 'count', label: t('Aantal') }, { value: 'lift', label: t('Lift') }]} />}>
              <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
                <span>{t("min. samen")}:</span>
                <input type="range" min="10" max="200" step="5" value={minCount}
                  onChange={(e) => setMinCount(+e.target.value)} className="flex-1 accent-[#cf9a40]" />
                <span className="tabular-nums w-10">{minCount}×</span>
              </div>
              <div ref={wrapRef} className="w-full">
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', aspectRatio: `${W} / ${H}`, display: 'block' }}
                onClick={() => setSelected(null)}>
                {shown.map((e, i) => {
                  const a = pos[e.source], b = pos[e.target]; if (!a || !b) return null;
                  const dim = neighbors && !(neighbors.has(e.source) && neighbors.has(e.target));
                  const hot = active && (e.source === active || e.target === active);
                  return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke={hot ? CALUM.gold : '#94a3b8'} strokeWidth={ew(e) * Math.sqrt(k)}
                    strokeOpacity={dim ? 0.05 : hot ? 0.75 : 0.22} strokeLinecap="round" />;
                })}
                {nodes.map((n) => {
                  const pt = pos[n.id]; if (!pt) return null;
                  const dim = neighbors && !neighbors.has(n.id);
                  const isActive = active === n.id;
                  const rad = r(n.count);
                  return (
                    <g key={n.id} transform={`translate(${pt.x},${pt.y})`} style={{ cursor: 'pointer' }}
                      onMouseEnter={CAN_HOVER ? () => setHover(n.id) : undefined}
                      onMouseLeave={CAN_HOVER ? () => setHover(null) : undefined}
                      onClick={(ev) => { ev.stopPropagation(); setSelected((s) => (s === n.id ? null : n.id)); }}
                      opacity={dim ? 0.25 : 1}>
                      {/* invisible, larger hit area so small bubbles stay tappable on phones */}
                      <circle r={Math.max(rad, 12 * k)} fill="transparent" />
                      <circle r={rad} fill={catColor(n.category)} stroke={selected === n.id ? CALUM.green : '#fff'} strokeWidth={isActive ? 3 : 1.5} />
                      {(rad > 12 || isActive || (neighbors && neighbors.has(n.id))) && (
                        <text textAnchor="middle" y={rad + 11 * k} fontSize={(isActive ? 12 : 10) * k}
                          fontWeight={isActive ? 700 : 500} fill="#334155">{n.name.length > 18 ? n.name.slice(0, 17) + '…' : n.name}</text>
                      )}
                    </g>
                  );
                })}
              </svg>
              </div>
              <div className="flex flex-wrap gap-3 mt-1">
                {cats.map((c) => (
                  <span key={c} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: catColor(c) }} />{t(CAT_LABEL[c] || c)}
                  </span>
                ))}
              </div>
            </SectionCard>

            <SectionCard title={t("Sterkste combinaties")}
              subtitle={active ? `${t("Relaties van")} ${nameOf(active)}` : t('Top 10 · vaakst samen gekocht')}
              right={selected && (
                <button onClick={() => setSelected(null)} className="text-[11px] text-slate-500 underline hover:text-slate-700 py-1">{t('wis selectie')}</button>
              )}>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                  <th className="py-1">{t("Combinatie")}</th><th className="text-right">{t("Samen")}</th><th className="text-right">{t("Lift")}</th>
                </tr></thead>
                <tbody>
                  {(active ? edges.filter((e) => e.source === active || e.target === active).sort((a, b) => b.count - a.count).slice(0, 12) : strongest).map((e, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-1.5 text-slate-700">{nameOf(e.source)} <span className="text-[#cf9a40] font-bold">+</span> {nameOf(e.target)}</td>
                      <td className="text-right tabular-nums">{formatNumber(e.count)}</td>
                      <td className="text-right tabular-nums">{e.lift}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                <b>{t("Lift")}</b> {t("= hoe veel vaker dan toeval. Lift 11 = 11× vaker samen dan je puur op kans zou verwachten; rond 1 = geen verband.")}
              </p>
            </SectionCard>
          </div>
        </>
        )}
      </QueryState>
    </PageShell>
  );
}
